import { colorsPalettes } from "@similarweb/styles";

import { cpcFilter, minVisitsAbbrFilter, percentageSignFilter } from "app/filters/ngFilters";
import type { IAxis, IChartOptionsMetaData } from "app/pages/industry-analysis/players/table/bubble-chart/types";
import { ETypes } from "app/pages/industry-analysis/players/table/bubble-chart/types";
import type { ICSS } from "app/UtilitiesAndConstants/types/cssTypes";

import { getUseDomainsOnly } from "../../utils/getUseDomainsOnly";

const { sky, orange } = colorsPalettes;
export const INITIAL_SELECTION = 10;
export const INITIAL_SELECTION_PERIOD_OVER_PERIOD = 5;
export const MAX_SELECTION = 15;
export const DEFAULT_COLOR = sky[400];
export const COMPARE_COLOR = orange[400];

/**
 * controls the min/max diameter of the bubbles
 * */
export const MIN_DIAMETER = 30;
export const MAX_DIAMETER = 80;
export const QUADRANT_BACKGROUND_IMAGE_PATH = "/images/scatter/quadrant-background-with-frame.svg";

export const CHART_HEIGHT: ICSS = { length: 442, unit: "px" };

const zAxis: IAxis = {
    tooltipTitleKey: "market.research.players.bubble.z.axis.tooltip",
    formatter: minVisitsAbbrFilter(),
    attribute: "AvgMonthVisits",
    comparedAttribute: "AvgMonthVisitsPrev",
};

const zAxisUnique: IAxis = {
    tooltipTitleKey: "market.research.players.bubble.z.axis.unique.tooltip",
    formatter: minVisitsAbbrFilter(),
    attribute: "AvgUniqueUsers",
};

const numbersFilter = (value) => (value >= 0 ? minVisitsAbbrFilter()(value) : null);
export const CHART_OPTIONS_META_DATA: Record<ETypes, IChartOptionsMetaData> = {
    [ETypes.BrandStrength]: {
        supportsIndustryWithSegments: true,
        quadrantNameKeys: {
            topLeft: "market.research.players.bubble.quadrant.name.top.left",
            topRight: "market.research.players.bubble.quadrant.name.top.right",
            bottomLeft: "market.research.players.bubble.quadrant.name.bottom.left",
            bottomRight: "market.research.players.bubble.quadrant.name.bottom.right",
        },
        xAxis: {
            titleKey: "market.research.players.bubble.x.axis.title",
            titleTooltipKey: "market.research.players.bubble.x.axis.title.tooltip",
            tooltipTitleKey: "market.research.players.bubble.x.axis.tooltip",
            formatter: numbersFilter,
            attribute: "DirectVisits",
        },
        yAxis: {
            titleKey: "market.research.players.bubble.y.axis.title",
            titleTooltipKey: "market.research.players.bubble.y.axis.title.tooltip",
            tooltipTitleKey: "market.research.players.bubble.y.axis.tooltip",
            formatter: numbersFilter,
            attribute: "BrandedVolume",
        },
        zAxis,
        chartTypeItems: {
            titleKey: "market.research.players.bubble.action.buttons.chart.type.brand.strength",
            tooltipKey: "market.research.players.bubble.action.buttons.chart.type.brand.strength.tooltip",
        },
    },
    [ETypes.CostEfficiency]: {
        supportsIndustryWithSegments: true,
        quadrantNameKeys: {
            topLeft: "market.research.players.bubble.cost.quadrant.name.top.left",
            topRight: "market.research.players.bubble.cost.quadrant.name.top.right",
            bottomLeft: "market.research.players.bubble.cost.quadrant.name.bottom.left",
            bottomRight: "market.research.players.bubble.cost.quadrant.name.bottom.right",
        },
        xAxis: {
            titleKey: "market.research.players.bubble.cost.x.axis.title",
            titleTooltipKey: "market.research.players.bubble.cost.x.axis.title.tooltip",
            tooltipTitleKey: "market.research.players.bubble.cost.x.axis.tooltip",
            formatter: (value) => percentageSignFilter()(value, 2),
            attribute: "AdjacentPeriodChange",
        },
        yAxis: {
            titleKey: "market.research.players.bubble.cost.y.axis.title",
            titleTooltipKey: "market.research.players.bubble.cost.y.axis.title.tooltip",
            tooltipTitleKey: "market.research.players.bubble.cost.y.axis.tooltip",
            formatter: (value) => (value >= 0 ? cpcFilter()(value) : null),
            attribute: "PpcSpend",
        },
        zAxis,
        chartTypeItems: {
            titleKey: "market.research.players.bubble.action.buttons.chart.type.cost.efficiency",
            tooltipKey: "market.research.players.bubble.action.buttons.chart.type.cost.efficiency.tooltip",
        },
    },
    [ETypes.AudienceGrowth]: {
        supportsIndustryWithSegments: true,
        isSegmentsEnabled: true,
        quadrantNameKeys: {
            topLeft: "market.research.players.bubble.audience.quadrant.name.top.left",
            topRight: "market.research.players.bubble.audience.quadrant.name.top.right",
            bottomLeft: "market.research.players.bubble.audience.quadrant.name.bottom.left",
            bottomRight: "market.research.players.bubble.audience.quadrant.name.bottom.right",
        },
        xAxis: {
            titleKey: "market.research.players.bubble.audience.x.axis.title",
            titleTooltipKey: "market.research.players.bubble.audience.x.axis.title.tooltip",
            tooltipTitleKey: "market.research.players.bubble.audience.x.axis.tooltip",
            formatter: numbersFilter,
            attribute: "AvgUniqueUsers",
        },
        yAxis: {
            titleKey: "market.research.players.bubble.audience.y.axis.title",
            titleTooltipKey: "market.research.players.bubble.audience.y.axis.title.tooltip",
            tooltipTitleKey: "market.research.players.bubble.audience.y.axis.tooltip",
            formatter: (value) => percentageSignFilter()(value, 2),
            attribute: "PeriodChange",
        },
        zAxis,
        chartTypeItems: {
            titleKey: "market.research.players.bubble.action.buttons.chart.type.audience",
            tooltipKey: "market.research.players.bubble.action.buttons.chart.type.audience.tooltip",
        },
    },
    [ETypes.Loyalty]: {
        get supportsIndustryWithSegments() {
            return getUseDomainsOnly();
        },
        quadrantNameKeys: {
            topLeft: "market.research.players.bubble.loyalty.quadrant.name.top.left",
            topRight: "market.research.players.bubble.loyalty.quadrant.name.top.right",
            bottomLeft: "market.research.players.bubble.loyalty.quadrant.name.bottom.left",
            bottomRight: "market.research.players.bubble.loyalty.quadrant.name.bottom.right",
        },
        xAxis: {
            titleKey: "market.research.players.bubble.loyalty.x.axis.title",
            titleTooltipKey: "market.research.players.bubble.loyalty.x.axis.title.tooltip",
            tooltipTitleKey: "market.research.players.bubble.loyalty.x.axis.tooltip",
            formatter: (value) => percentageSignFilter()(value, 2),
            attribute: "ExclusiveVisitors",
        },
        yAxis: {
            titleKey: "market.research.players.bubble.loyalty.y.axis.title",
            titleTooltipKey: "market.research.players.bubble.loyalty.y.axis.title.tooltip",
            tooltipTitleKey: "market.research.players.bubble.loyalty.y.axis.tooltip",
            formatter: (value) => percentageSignFilter()(value, 2),
            attribute: "NewVsReturning",
        },
        zAxis: zAxisUnique,
        chartTypeItems: {
            titleKey: "market.research.players.bubble.action.buttons.chart.type.loyalty",
            tooltipKey: "market.research.players.bubble.action.buttons.chart.type.loyalty.tooltip",
        },
    },
    [ETypes.ConversionRate]: {
        supportsIndustryWithSegments: true,
        quadrantNameKeys: {
            topLeft: "market.research.players.bubble.conversion.quadrant.name.top.left",
            topRight: "market.research.players.bubble.conversion.quadrant.name.top.right",
            bottomLeft: "market.research.players.bubble.conversion.quadrant.name.bottom.left",
            bottomRight: "market.research.players.bubble.conversion.quadrant.name.bottom.right",
        },
        xAxis: {
            titleKey: "market.research.players.bubble.conversion.x.axis.title",
            titleTooltipKey: "market.research.players.bubble.conversion.x.axis.title.tooltip",
            tooltipTitleKey: "market.research.players.bubble.conversion.x.axis.tooltip",
            formatter: (value) => percentageSignFilter()(value, 2),
            attribute: "Share",
        },
        yAxis: {
            titleKey: "market.research.players.bubble.conversion.y.axis.title",
            titleTooltipKey: "market.research.players.bubble.conversion.y.axis.title.tooltip",
            tooltipTitleKey: "market.research.players.bubble.conversion.y.axis.tooltip",
            formatter: (value) => percentageSignFilter()(value, 2),
            attribute: "ConversionRate",
        },
        zAxis,
        chartTypeItems: {
            titleKey: "market.research.players.bubble.action.buttons.chart.type.conversion.rate",
            tooltipKey: "market.research.players.bubble.action.buttons.chart.type.conversion.rate.tooltip",
        },
    },
};
